import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Spinner from 'components/Spinner';
import { useSelector } from 'react-redux';

import AlertTable from '../AlertItem/AlertTable';
import {
  ALERT_CATEGORY_ASSET_MANAGEMENT,
  ALERT_CATEGORY_PROPERTY_MANAGEMENT,
  ALERT_TYPE_OPEX_COLS_DEF,
  ALERT_TYPE_OPEX_CONTRACT_EXPIRATION,
  ALERT_TYPE_TENANT_CONTRACT_BREAK,
  ALERT_TYPE_TENANT_CONTRACT_BREAK_COLS_DEF,
  ALERT_TYPE_TENANT_CONTRACT_EXPIRATION,
  ALERT_TYPE_TENANT_CONTRACT_EXPIRATION_COLS_DEF,
  ALERT_TYPE_VACANT_UNITS,
  ALERT_TYPE_VACANT_UNITS_COLS_DEF
} from './config';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2)
  },
  alertTitle: {
    fontFamily: theme.fonts.base,
    color: '#000000',
    textTransform: 'uppercase',
    fontWeight: 500,
    paddingBottom: theme.spacing(1)
  }
}));


const SimpleTables = () => {
  const classes = useStyles();
  const loading = useSelector((state) => state.alerts.loading);
  const { alerts } = useSelector((state) => state.alerts);
  const { assetIds: selectedAssetIds } = useSelector((state) => state.alerts.sidebarFilters);

  const filteredAlerts = useMemo(
    () =>
      selectedAssetIds === null
        ? alerts
        : alerts.filter((it) => selectedAssetIds.includes(it.assetid)),
    [selectedAssetIds, alerts]
  );

  const assetAlerts = useMemo(
    () => filteredAlerts.filter((alert) => alert.category === ALERT_CATEGORY_ASSET_MANAGEMENT),
    [filteredAlerts]
  );
  const propertyAlerts = useMemo(
    () => filteredAlerts.filter((alert) => alert.category === ALERT_CATEGORY_PROPERTY_MANAGEMENT),
    [filteredAlerts]
  );

  const tenantContractExpirationData = useMemo(
    () => assetAlerts
      .filter((alert) => alert.type === ALERT_TYPE_TENANT_CONTRACT_EXPIRATION)
      .map((alert) => ({...alert, leaseEndDate: alert.lease.enddate * 1000})),
    [assetAlerts]
  );

  const tenantContractBreakData = useMemo(
    () => assetAlerts
      .filter((alert) => alert.type === ALERT_TYPE_TENANT_CONTRACT_BREAK)
      .map((alert) => ({...alert, leaseEndDate: alert.lease.enddate * 1000})),
    [assetAlerts]
  );

  const vacantUnitsData = useMemo(
    () => assetAlerts
      .filter((alert) => alert.type === ALERT_TYPE_VACANT_UNITS)
      .map((alert) => ({...alert, unit: alert.vacantunits.name, gla: alert.vacantunits.gla})),
    [assetAlerts]
  );

  const opexData = useMemo(
    () => assetAlerts
      .filter((alert) => alert.type === ALERT_TYPE_OPEX_CONTRACT_EXPIRATION)
      .map((alert) => ({
        ...alert,
        opexName: alert.opex.opexname,
        supplier: alert.opex.supplier,
        opexContractedData: alert.opex.contractenddate
      })),
    [propertyAlerts]
  );

  if (loading) {
    return (
      <Box pt={10}>
        <Spinner mini />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <AlertTable
        title={'Vacant Units'}
        alerts={vacantUnitsData}
        colDefs={ALERT_TYPE_VACANT_UNITS_COLS_DEF}
      />
      <Box style={{marginTop: "20px"}}>
        <AlertTable
          title={'Upcoming Lease Expiry'}
          alerts={tenantContractExpirationData}
          colDefs={ALERT_TYPE_TENANT_CONTRACT_EXPIRATION_COLS_DEF}
        />
      </Box>
      <Box style={{marginTop: "20px"}}>
        <AlertTable
          title={'Upcoming Lease Break'}
          alerts={tenantContractBreakData}
          colDefs={ALERT_TYPE_TENANT_CONTRACT_BREAK_COLS_DEF}
        />
      </Box>
      <Box style={{marginTop: "20px"}}>
        <AlertTable
          title={'Opex Contract Expires'}
          alerts={opexData}
          colDefs={ALERT_TYPE_OPEX_COLS_DEF}
        />
      </Box>
    </div>
  );
};

export default SimpleTables;
